import "@/app/globals.css";
import "react-toastify/dist/ReactToastify.css";
import AppDrawerWrapper from "@/layouts/components/AppDrawerWrapper";
import { ToastContainer } from "react-toastify";
import Breadcrumbs from "@/layouts/components/Breadcrumbs";
import { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { PING } from "@/graphql/queries/auth";
import PingLoader from "@/layouts/components/PingLoader";
import Impersonation from "@/components/base/misc/Impersonation";
import { useImpersonation } from "./components/layoutContext";
import { localStorageWrapper } from "@/utils/storage";
import { Box, useMediaQuery } from "@mui/material";
import { useRouter } from "next/router";
import Gofitno from "@/components/base/support/Goftino";
import { useAppGlobal } from "@/components/base/app/AppGlobalProvider";
import TopBanner from "@/components/base/app/TopBanner";
import { useTheme } from "@emotion/react";
import { GroupsProvider } from "@/components/course/lesson/hooks/useGroups";
import { LessonsProvider } from "@/components/course/lesson/hooks/useLessons";
import useChatWidgetRouter from "@/components/base/support/useChatWidgetRouter";
import OnlineHawzahChatWidget from "@/components/base/support/OnlineHawzahChatWidget";
import useWidgetTokenUpdate from "@/components/base/hooks/useWidgetTokenUpdate";

export default function DefaultLayout({ children, title }) {
    const [InitialtokenChecked, setInitialTokenChecked] = useState(false);

    const [impersonated, setImpersonate] = useImpersonation();

    const [ping, { loading }] = useLazyQuery(PING, {
        fetchPolicy: "network-only",
    });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(600));

    const router = useRouter();

    useWidgetTokenUpdate(InitialtokenChecked);

    useEffect(() => {
        document.body.className = "default-layout";
    }, []);

    useEffect(() => {
        if (router.pathname === "/oauth") {
            // Only skip ping request in /oauth
            // because oauth itself call backend
            // with auth token to check if user is logged in
            // and therefore no need to ping the backend
            setInitialTokenChecked(true);
        } else {
            ping({
                onCompleted: ({ ping }) => {
                    if (ping === "pong") setInitialTokenChecked(true);
                },
                onError: (error) => {
                    if (
                        error.message ===
                        "You do not have permission to perform this action"
                    ) {
                        // If user cannot ping then he/she dose not exists
                        router.push("/login");
                    }
                },
            });
        }

        if (!localStorageWrapper.isUserRegistered()) {
            // if user logged in
            // it means user is already registered
            // then set user registered flag
            // to prevent show the reg page to the user
            // when opening the site on the main path /
            localStorageWrapper.setUserRegistered();
        }

        if (localStorageWrapper.isImpersonated()) {
            setImpersonate(true);
        }
    }, []);

    const {
        setGoftinoIsReady,
        toggleGoftinoOpen,
        toggleGoftinoClose,
        goftinoSendMessageOcurred,
        goftinoGetMessageOcurred,
    } = useAppGlobal();

    const activeChatWidget = useChatWidgetRouter();

    if (!InitialtokenChecked || loading) {
        return <PingLoader />;
    }

    return (
        <GroupsProvider>
            <LessonsProvider>
                <Box sx={{ position: "absolute", top: 0, right: 0, left: 0 }}>
                    {impersonated && <Impersonation />}

                    {isMobile && (
                        <TopBanner onClick={(route) => router.push(route)} />
                    )}

                    <AppDrawerWrapper title={title}>
                        {!isMobile && (
                            <TopBanner
                                onClick={(route) => router.push(route)}
                            />
                        )}

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                position: "relative",
                                padding: {
                                    xs: "0 4px",
                                    sm: "0 8px",
                                },
                            }}
                        >
                            <Breadcrumbs />
                            <Box sx={{ margin: "0px 8px" }}>{children}</Box>
                        </Box>

                        <ToastContainer
                            position="bottom-left"
                            autoClose={5000}
                            hideProgressBar={false}
                            newestOnTop={false}
                            closeOnClick
                            rtl
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                            theme="light"
                            style={{ bottom: 10, zIndex: 1000001 }}
                        />
                    </AppDrawerWrapper>

                    {activeChatWidget === "OnlineHawzahChatWidget" && (
                        <OnlineHawzahChatWidget />
                    )}

                    {activeChatWidget === "Goftino" && (
                        <Gofitno
                            onReady={() => {
                                console.debug("*** Goftino is ready ***");
                                setGoftinoIsReady();
                            }}
                            onOpen={() => {
                                console.debug("*** Goftino is opened ***");
                                toggleGoftinoOpen();
                            }}
                            onClose={() => {
                                console.debug("*** Goftino is closed ***");
                                toggleGoftinoClose();
                            }}
                            onSendMessage={(d) => {
                                console.debug(
                                    "*** Goftino user sends message ***"
                                );
                                goftinoSendMessageOcurred();
                            }}
                            onGetMessage={(d) => {
                                console.debug(
                                    "*** Goftino user gets message ***"
                                );
                                goftinoGetMessageOcurred();
                            }}
                        />
                    )}
                </Box>
            </LessonsProvider>
        </GroupsProvider>
    );
}
